import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Loader2, CheckCircle2, Trophy, Sparkles, Clock } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Skeleton } from '../components/ui/skeleton';
import { Textarea } from '../components/ui/textarea';
import api from '../services/api';
import { toast } from 'sonner';

const CLAIM_OPTIONS = [
  {
    value: 'avanco_diario',
    label: 'Avanço diário',
    icon: Sparkles,
    points: '1.250 pontos',
    description: 'Você avançou na sua meta hoje.',
  },
  {
    value: 'pequena_meta',
    label: 'Pequena meta concluída',
    icon: CheckCircle2,
    points: '5.000 pontos',
    description: 'Um objetivo do seu plano, fechado.',
  },
  {
    value: 'grande_meta',
    label: 'Grande meta concluída',
    icon: Trophy,
    points: '50.000 pontos',
    description: 'Um marco de peso do seu plano. Passa pela avaliação do ELIOS.',
  },
  {
    value: 'conexao_positiva',
    label: 'Conexão positiva com outro membro',
    icon: Sparkles,
    points: '2.000 pontos',
    description: 'Você ajudou, apresentou ou destravou alguém da Elite.',
  },
];

const GamificationFillPage = () => {
  const { gamificationId } = useParams();

  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [claimedType, setClaimedType] = useState('');
  const [description, setDescription] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    const loadRecord = async () => {
      if (!gamificationId) {
        setLoadError('Link de gamificação inválido.');
        setLoading(false);
        return;
      }

      setLoading(true);
      setLoadError('');

      try {
        await api.get(`/gamification/link/${gamificationId}`);
      } catch (err) {
        const fallbackMessage = 'Não foi possível carregar este convite. Verifique se o link existe ou se já foi respondido.';
        setLoadError(err?.response?.data?.detail || fallbackMessage);
      } finally {
        setLoading(false);
      }
    };

    loadRecord();
  }, [gamificationId]);

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!claimedType) {
      toast.error('Escolha o tipo de conquista antes de continuar.');
      return;
    }

    if (description.trim().length < 5) {
      toast.error('Conte um pouco mais sobre o que você fez (mínimo 5 caracteres).');
      return;
    }

    setSubmitting(true);

    try {
      const response = await api.post(`/gamification/submit/${gamificationId}`, {
        claimed_type: claimedType,
        description: description.trim(),
      });
      setResult(response.data);
    } catch (err) {
      const fallbackMessage = 'Não foi possível enviar sua conquista agora. Tente novamente em alguns instantes.';
      if (err?.response?.status === 410) {
        setLoadError(err.response.data.detail);
      }
      toast.error(err?.response?.data?.detail || fallbackMessage);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen login-bg flex items-center justify-center p-4">
        <div className="grid-overlay" />
        <Card className="w-full max-w-2xl glass-card border-white/10 z-10">
          <CardHeader>
            <Skeleton className="h-8 w-72 bg-white/10" />
            <Skeleton className="h-5 w-96 bg-white/10" />
          </CardHeader>
          <CardContent className="space-y-4">
            <Skeleton className="h-24 w-full bg-white/10" />
            <Skeleton className="h-24 w-full bg-white/10" />
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen login-bg flex items-center justify-center p-4">
      <div className="grid-overlay" />

      <Card className="w-full max-w-2xl glass-card border-white/10 z-10">
        <CardHeader>
          <CardTitle className="text-white text-2xl md:text-3xl">Conquista do Dia</CardTitle>
          <CardDescription className="text-slate-300">
            Registre algo que você fez de bom hoje e ganhe pontos.
          </CardDescription>
        </CardHeader>

        <CardContent>
          {result ? (
            <div
              className={`rounded-xl border p-8 text-center ${
                result.status === 'awaiting_evaluation'
                  ? 'border-amber-400/30 bg-amber-400/10'
                  : 'border-emerald-400/30 bg-emerald-400/10'
              }`}
            >
              <div
                className={`mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full ${
                  result.status === 'awaiting_evaluation' ? 'bg-amber-400/20' : 'bg-emerald-400/20'
                }`}
              >
                {result.status === 'awaiting_evaluation' ? (
                  <Clock className="h-8 w-8 text-amber-300" />
                ) : result.final_type === 'grande_meta' ? (
                  <Trophy className="h-8 w-8 text-emerald-300" />
                ) : (
                  <Sparkles className="h-8 w-8 text-emerald-300" />
                )}
              </div>
              <h2 className="text-2xl font-semibold text-white mb-2">
                {result.status === 'awaiting_evaluation' ? 'Recebemos sua conquista!' : 'Conquista registrada'}
              </h2>
              <p className="text-slate-200">{result.feedback_message}</p>
            </div>
          ) : loadError ? (
            <div className="rounded-lg border border-red-500/40 bg-red-500/10 p-4 text-red-200">{loadError}</div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid gap-3 sm:grid-cols-2">
                {CLAIM_OPTIONS.map((option) => {
                  const Icon = option.icon;
                  const isSelected = claimedType === option.value;
                  return (
                    <button
                      key={option.value}
                      type="button"
                      onClick={() => setClaimedType(option.value)}
                      className={`rounded-lg border p-4 text-left transition-colors ${
                        isSelected
                          ? 'border-primary bg-primary/10'
                          : 'border-white/10 bg-black/20 hover:bg-white/5'
                      }`}
                    >
                      <div className="mb-2 flex items-center gap-2">
                        <Icon className={`h-5 w-5 ${isSelected ? 'text-primary' : 'text-slate-300'}`} />
                        <span className="font-semibold text-white">{option.label}</span>
                      </div>
                      <p className="text-xs font-medium text-slate-400 mb-1">{option.points}</p>
                      <p className="text-sm text-slate-300">{option.description}</p>
                    </button>
                  );
                })}
              </div>

              <div className="space-y-2">
                <label htmlFor="gamification-description" className="text-sm font-medium text-slate-200">
                  Conte o que você fez
                </label>
                <Textarea
                  id="gamification-description"
                  value={description}
                  onChange={(event) => setDescription(event.target.value)}
                  maxLength={1000}
                  rows={4}
                  placeholder="Ex.: Fui caminhar e consegui superar minha meta do dia."
                  className="bg-black/20 border-white/10 text-white placeholder:text-slate-500"
                />
                <p className="text-xs text-slate-500 text-right">{description.length}/1000</p>
              </div>

              <div className="flex justify-end pt-2">
                <Button type="submit" className="min-w-48" disabled={submitting}>
                  {submitting ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Enviando...
                    </>
                  ) : (
                    'Registrar Conquista'
                  )}
                </Button>
              </div>
            </form>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default GamificationFillPage;
